// ============================================================
// Grinding plan from energy prices (#74, concept): when to run each
// mill tomorrow so it uses the cheapest OTE hours. The cost and the
// saving are always computed here, never taken from the model.
// Plain JS: the Worker imports it.
// ============================================================

// Demo mills: power draw, hours of grinding needed tomorrow, hours when staff can run them.
export const MILLS = [
  { id: 'M1', name: 'Mlýn 1 (Omyacarb 2-AV)', powerMW: 2.4, hoursNeeded: 10, allowed: [0, 23] },
  { id: 'M2', name: 'Mlýn 2 (Omyacarb 10-AV)', powerMW: 1.8, hoursNeeded: 8, allowed: [0, 23] },
  { id: 'M3', name: 'Mlýn 3 (big bag)', powerMW: 1.2, hoursNeeded: 6, allowed: [6, 21] },
];

// Today's habit: every mill starts with the morning shift at 6:00.
export const baselineHours = (mill) => Array.from({ length: mill.hoursNeeded }, (_, i) => (6 + i) % 24);

const inWindow = (h, [from, to]) => h >= from && h <= to;
const costOf = (hours, mill, prices) => hours.reduce((s, h) => s + prices[h] * mill.powerMW, 0);

// Keeps only valid hours for each mill; null if a mill's plan is unusable.
export function validPlan(schedule, mills = MILLS) {
  const plan = mills.map(m => {
    const hours = [...new Set((schedule.find(s => s.millId === m.id)?.hours || []).filter(h => Number.isInteger(h) && inWindow(h, m.allowed)))];
    return hours.length === m.hoursNeeded ? { millId: m.id, hours: hours.sort((a, b) => a - b) } : null;
  });
  return plan.every(Boolean) ? plan : null;
}

// CZK for the plan and for today's habit, per mill and in total.
export function evaluatePlan(schedule, prices, mills = MILLS) {
  const rows = mills.map(m => {
    const hours = schedule.find(s => s.millId === m.id).hours;
    return { millId: m.id, cost: costOf(hours, m, prices), baseline: costOf(baselineHours(m), m, prices) };
  });
  const cost = rows.reduce((s, r) => s + r.cost, 0);
  const baseline = rows.reduce((s, r) => s + r.baseline, 0);
  return { rows, cost, baseline, saving: baseline - cost };
}

// Prepared answer without a key: each mill takes its cheapest allowed hours.
export function energyPlanDemo({ prices, lang }) {
  const schedule = MILLS.map(m => ({
    millId: m.id,
    hours: prices.map((p, h) => ({ p, h })).filter(x => inWindow(x.h, m.allowed))
      .sort((a, b) => a.p - b.p).slice(0, m.hoursNeeded).map(x => x.h).sort((a, b) => a - b),
  }));
  const cs = lang === 'cs';
  return {
    schedule,
    summary: cs
      ? 'Mlýny jedou v nejlevnějších hodinách: v noci a v poledním okně FVE, mimo odpolední špičku.'
      : 'The mills run in the cheapest hours: at night and in the midday solar window, outside the afternoon peak.',
    notes: cs
      ? ['Mlýn 3 jen 6–21 h (obsluha).', 'Ověřit zásobu v silech pro ranní nakládky.']
      : ['Mill 3 only 6–21 h (staff).', 'Check the silo stock for the morning loadings.'],
  };
}
